// In-round HUD (§14.3). Top-center: round pill (GOLDEN ROUND in gold), the timer counting down to
// the cap, the phase chip (CALM / HAZARDS UP / SUDDEN DEATH) and the active mutator. Top-left: the
// mode widget. Top-right: the kill feed and the connection icon. Bottom-center: the Dizzy Dial
// with the shove pip and item bubble. World-space nameplates, the mash prompt and offscreen
// arrows; the banner layer; the hold-to-show scoreboard; first-time prompts and the Practice bar.
// Per-frame DOM writes are diffed (§27.2). Sim events are taken only while the HUD is shown, and
// replayed events never reach the feed or banners.
import { MAX_BEANS, NO_SLOT, TEAM_RESULT } from '@shared/constants';
import { TEAM_STYLES } from '@shared/cosmetics';
import { EV, KoFlag, type SimEvent } from '@shared/events';
import { ItemKind, MutatorId, RoomPhase, RoundPhase } from '@shared/ids';
import { onSim } from '../../core/bus';
import { createFrameView, type FrameView } from '../../game/FrameView';
import type { LiveScreen, NavAction } from '../contracts';
import type { HudInsets } from '../../render/camera/framing';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { icon, identityBadge, ITEM_ICONS, MUTATOR_ICONS } from '../icons';
import { clockText, ITEM_NAMES, MODE_NAMES, MUTATOR_NAMES, PHASE_NAMES, mapName } from '../labels';
import { el, replayClass, setHidden, setStyle, setText, toggleClass } from '../ui';
import { button, pingBars } from '../widgets';
import { replayOverlay } from '../screens/ReplayOverlay';
import { createBanners } from './Banners';
import { createDizzyDial } from './DizzyDial';
import { createKillFeed } from './KillFeed';
import { createModeWidget } from './ModeWidget';
import { createNameplates } from './Nameplates';
import { createPromptView } from './PromptView';
import { createScoreboard } from './Scoreboard';

/** How often (frames) the HUD re-measures its footprint for the camera framing (INT-B). */
const INSETS_EVERY = 30;
/** Footprint changes smaller than this (viewport fraction) are not reported. */
const INSETS_EPS = 0.004;

/** Needle kick (deg/s) per unit of knockback when the local bean is hit. */
const KICK_PER_KB = 34;
const URGENT_SEC = 10;
/** The last round's result stamp follows GAME!'s stamp-in (ROUND_END is only 1 s before the Awards). */
const AFTER_GAME_MS = 450;
const PRACTICE_ITEMS: readonly ItemKind[] = [ItemKind.BANANA, ItemKind.BOMB, ItemKind.GLOVE, ItemKind.BALLOON, ItemKind.PLUNGER, ItemKind.SHAKE];

export function create(deps: UiDeps): LiveScreen {
  // ---- Top-center: round pill, timer, phase chip, mutator ------------------------------------
  const roundPill = el('span', { class: 'hud-round' });
  const timer = el('span', { class: 'hud-timer' });
  const phaseChip = el('span', { class: 'hud-phase' });
  const mutChip = el('span', { class: 'hud-mutator' });
  const hazardsUp = el('div', { class: 'hud-hazards-up' }, icon('sparkle'), el('span', { text: 'HAZARDS UP!' }));
  hazardsUp.hidden = true;
  const top = el('div', { class: 'hud-top' }, el('div', { class: 'hud-top-row' }, roundPill, timer, phaseChip), mutChip, hazardsUp);

  // ---- Corners -------------------------------------------------------------------------------
  const mode = createModeWidget();
  const scoreboard = createScoreboard(deps);
  // Tapping (or clicking) the mode widget toggles the scoreboard: touch has no Tab / View.
  const modeWrap = el('div', { class: 'hud-left', attrs: { title: 'Scoreboard' }, on: { click: () => scoreboard.toggle() } }, mode.el);
  const pauseBtn = button({ icon: 'pause', title: 'Menu', variant: 'cream', size: 'small', cls: 'round hud-pause', onClick: () => deps.nav.show('pause') });
  const feed = createKillFeed();
  const netIcon = el('div', { class: 'hud-net' }, icon('wifi'), el('span', { class: 'hn-ms' }));
  const right = el('div', { class: 'hud-right' }, feed.el, netIcon);
  // HONK / EMOTE sit right under the kill feed on touch (WP9a): reserve its full 5-line height.
  /** The shell's compact or narrow layout (phones, or a large UI scale): 3 kill-feed lines. */
  function compactLayout(): boolean {
    const ui = root.closest('#ui');
    return ui !== null && (ui.classList.contains('compact') || ui.classList.contains('narrow'));
  }
  let feedReserve = -1;
  function reserveFeed(): void {
    const line = feed.el.querySelector<HTMLElement>('.kf-line');
    if (!line || deps.services.input.device !== 'touch') return;
    const lines = compactLayout() ? 3 : 5;
    const gap = parseFloat(getComputedStyle(feed.el).rowGap) || 0;
    const px = Math.ceil(right.getBoundingClientRect().top + lines * (line.offsetHeight + gap));
    if (px !== feedReserve) {
      feedReserve = px;
      deps.services.input.touch.setKillFeedHeight(px);
    }
  }
  window.addEventListener('resize', () => {
    feedReserve = -1;
  });

  // ---- Bottom: dial, prompts, practice bar, hints --------------------------------------------
  const dial = createDizzyDial(deps);
  const prompt = createPromptView(deps, 'hud-prompt');
  const practiceTitle = el('span', { class: 'prac-title' });
  const practiceItems = el('div', { class: 'prac-items' });
  const practiceHint = el('div', { class: 'prac-hint' });
  const practice = el('div', { class: 'practice-bar' }, el('div', { class: 'prac-head' }, el('span', { class: 'chip mint prac-chip', text: 'PRACTICE' }), practiceTitle), practiceItems, practiceHint);
  const scoreHint = el('div', { class: 'hud-score-hint' });
  const bottom = el('div', { class: 'hud-bottom' }, prompt.el, dial.el);

  const banners = createBanners();
  const plates = createNameplates(deps);
  // BONK CAM (§11) plays over the live round: its skip pill and tap target (the VHS look is the
  // Renderer's). The Dizzy Dial and the prompts step aside while any replay is on screen.
  const bonkCam = replayOverlay(deps, ['bonkcam']);

  const root = el('div', { class: 'screen hud', attrs: { 'data-nofocus': true } },
    plates.el, banners.el, top, modeWrap, pauseBtn, right, bottom, practice, scoreHint, scoreboard.el, bonkCam.el);

  let last: FrameView = createFrameView();
  /** Live events are taken only while the HUD is on the screen stack (WP0 fix: no stale feed lines). */
  let shown = false;
  let roundId = -1;
  let introFor = -1;
  let lastPhase = -1;
  let lastTimerSec = -1;
  const roundKos = new Array<number>(MAX_BEANS).fill(0);
  let lastFrame = 0;
  let gameTimer = 0;
  let insetFrames = 0;
  let sentInsets: HudInsets | null = null;

  function visibleRect(n: Element | null): DOMRect | null {
    if (!(n instanceof HTMLElement) || n.hidden || n.closest('[hidden]') || n.offsetParent === null) return null;
    const r = n.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  }

  /**
   * The HUD's footprint over the game view (INT-B's HUD-aware framing): the top bar's bottom edge;
   * at the bottom the Dizzy Dial (or the spectator bar) and how far it reaches from the middle;
   * and elsewhere along the bottom the touch controls or the corner chips.
   */
  function measureInsets(): HudInsets | null {
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (W <= 0 || H <= 0) return null;
    const t = visibleRect(top);
    const center = visibleRect(document.querySelector('.spectator .spec-bar')) ?? visibleRect(dial.el);
    let side = 0;
    const ui = root.closest<HTMLElement>('#ui');
    if (ui?.dataset.touchMode === 'full') {
      const css = getComputedStyle(ui);
      const tops = [css.getPropertyValue('--tc-cluster-top'), css.getPropertyValue('--tc-stick-top')].map((v) => parseFloat(v)).filter((v) => Number.isFinite(v) && v > 0);
      if (tops.length > 0) side = Math.max(0, H - Math.min(...tops));
    }
    for (const n of [practice, scoreHint]) {
      const r = visibleRect(n);
      if (r) side = Math.max(side, H - r.top);
    }
    const f = (v: number): number => Math.round(Math.min(0.6, Math.max(0, v)) * 1000) / 1000;
    return {
      top: f(t ? t.bottom / H : 0),
      bottom: f(center ? (H - center.top) / H : 0),
      bottomHalfWidth: f(center ? Math.max(Math.abs(center.left - W / 2), Math.abs(center.right - W / 2)) / W : 0),
      bottomSide: f(side / H),
    };
  }

  function reportInsets(force: boolean): void {
    if (!force && ++insetFrames < INSETS_EVERY) return;
    insetFrames = 0;
    // Replays hide the dial and use their own cameras: keep the gameplay footprint.
    if (root.classList.contains('replaying')) return;
    const h = measureInsets();
    if (!h) return;
    const s = sentInsets;
    if (s && Math.abs(s.top - h.top) < INSETS_EPS && Math.abs(s.bottom - h.bottom) < INSETS_EPS
      && Math.abs(s.bottomHalfWidth - h.bottomHalfWidth) < INSETS_EPS && Math.abs(s.bottomSide - h.bottomSide) < INSETS_EPS) return;
    sentInsets = h;
    deps.services.setHudInsets(h);
  }

  function nameOf(slot: number): string {
    return slot < MAX_BEANS ? last.beans[slot].name || `Bean ${slot + 1}` : '';
  }

  function colorblind(): boolean {
    return deps.settings().accessibility.colorblind;
  }

  function winnerStamp(ev: SimEvent, low = false): void {
    const sub = low ? 'FINAL ROUND' : 'ROUND OVER';
    if (ev.a === NO_SLOT) {
      banners.stamp('DRAW!', 'white', 'Survivors share the points', undefined, low);
      return;
    }
    if (ev.a === TEAM_RESULT) {
      const team = TEAM_STYLES[ev.b === 2 ? 2 : 1];
      banners.stamp(`${team.name.toUpperCase()} TEAM WINS!`, ev.b === 2 ? 'pink' : 'pink', sub, undefined, low);
      return;
    }
    const b = last.beans[ev.a];
    const me = ev.a === last.localSlot;
    banners.stamp([el('span', { class: 'st-place', text: '1st:' }), identityBadge(b?.identity ?? ev.a, colorblind(), 'idbadge lg'), el('span', { text: me ? 'YOU!' : `${nameOf(ev.a)}!` })], me ? 'gold' : 'yellow', sub, undefined, low);
  }

  function onEvent(ev: SimEvent, _local: boolean, replay: boolean): void {
    if (!shown || replay) return;
    const local = last.localSlot;
    const now = performance.now();
    switch (ev.type) {
      case EV.COUNTDOWN:
        banners.countdown(ev.b);
        break;
      case EV.PHASE:
        // The red vignette pulse is the Renderer's (FinalPass); the HUD names the moment. Once the
        // match is decided (GAME!), a late phase change is not announced.
        if (ev.b === RoundPhase.SUDDEN_DEATH) {
          if (last.grade !== 'game') banners.sweep('SUDDEN DEATH!', 'danger', 'The arena is shrinking!');
        } else if (ev.b === RoundPhase.RAMP) {
          setHidden(hazardsUp, false);
          replayClass(hazardsUp, 'go');
          setTimeout(() => setHidden(hazardsUp, true), 2600);
        }
        break;
      case EV.MATCH_POINT:
        // The letterbox bars are the Renderer's (FinalPass, under the HUD).
        if (last.grade !== 'game') banners.sweep('MATCH POINT', 'gold', 'Two beans left!');
        break;
      case EV.ROUND_OVER: {
        // The match's last round: GAME! (the Renderer's stamp, slow motion, desaturation) has the
        // moment; the result follows it, lower on the screen.
        const lastRound = last.source === 'online' && last.totalRounds > 0 && last.roundIndex === last.totalRounds - 1;
        if (!lastRound) {
          winnerStamp(ev);
          break;
        }
        const held = ev;
        gameTimer = window.setTimeout(() => {
          gameTimer = 0;
          if (shown) winnerStamp(held, true);
        }, AFTER_GAME_MS);
        break;
      }
      case EV.KO: {
        feed.push(ev, last.beans, local, colorblind());
        if (feedReserve < 0) requestAnimationFrame(reserveFeed);
        const self = (ev.d & KoFlag.SELF) !== 0 || ev.b === NO_SLOT || ev.b === ev.a;
        if (!self && ev.b < MAX_BEANS) roundKos[ev.b]++;
        const involved = local !== NO_SLOT && (ev.a === local || ev.b === local);
        // DOUBLE BONK! pops at the splash in the world (the Renderer's TextPops).
        if (involved && !self && (ev.d & KoFlag.REVENGE) !== 0) banners.sweep('REVENGE!', 'revenge', ev.b === local ? `You got ${nameOf(ev.a)} back!` : `${nameOf(ev.b)} got you back!`);
        else if (involved && !self && (ev.d & KoFlag.BOUNTY) !== 0) banners.sweep('BOUNTY!', 'gold', ev.b === local ? '+2 for toppling the leader!' : `${nameOf(ev.b)} claimed your crown`);
        break;
      }
      case EV.HIT:
        if (ev.b === local && local !== NO_SLOT) dial.kick((ev.d / 10) * KICK_PER_KB * (ev.x > 0 ? 1 : -1));
        break;
      case EV.LUNGE:
        if (ev.a === local) dial.shoveUsed(now);
        break;
      case EV.PEBBLE_DROP:
        if (ev.a === local) dial.pebbleUsed(now);
        break;
      case EV.GHOST_READY:
        if (ev.a === local) {
          dial.pebbleUsed(-Infinity);
          banners.stamp([el('span', { text: "YOU'RE A GHOST!" })], 'ghost', '', 2600);
          ghostHint();
        }
        break;
      case EV.POTATO_NEW:
      case EV.POTATO_PASS: {
        const holder = ev.type === EV.POTATO_NEW ? ev.a : ev.b;
        if (holder === local && local !== NO_SLOT) banners.sweep('HOT POTATO!', 'danger', 'Pass it on — bump someone!');
        break;
      }
      default:
        break;
    }
  }
  onSim(deps.bus, onEvent);

  /** "Shove = drop pebble" with the live glyph under the ghost stamp. */
  function ghostHint(): void {
    const sub = root.querySelector('.bn-stamp .st-sub');
    if (sub instanceof HTMLElement) {
      sub.hidden = false;
      sub.replaceChildren(actionCap(deps, 'shove'), el('span', { text: ' = drop a pebble' }));
    }
  }

  function startRound(view: FrameView): void {
    if (gameTimer) clearTimeout(gameTimer);
    gameTimer = 0;
    roundId = view.roundId;
    feed.clear();
    dial.reset();
    banners.reset();
    roundKos.fill(0);
  }

  function renderPractice(): void {
    const info = deps.services.practiceInfo();
    setHidden(practice, !info.active);
    if (!info.active || !info.setup) return;
    setText(practiceTitle, `${info.freePlay ? 'Free play' : MODE_NAMES[info.setup.modeId]} • ${mapName(info.setup.mapId)}`);
    const dev = deps.services.input.device;
    practiceItems.replaceChildren(...(dev === 'touch' ? [] : PRACTICE_ITEMS.map((k, i) => el('span', { class: 'prac-item', attrs: { title: ITEM_NAMES[k] } },
      el('span', { class: 'key', text: String(i + 1) }), icon(ITEM_ICONS[k])))));
    practiceHint.replaceChildren(...(dev === 'touch'
      ? [el('span', { text: 'Item wheel: top-right • ' }), el('span', { text: 'PAUSE to restart' })]
      : [el('span', { text: 'Grab any item • ' }), actionCap(deps, 'menu'), el('span', { text: ' restart / leave' })]));
  }

  function renderScoreHint(): void {
    const dev = deps.services.input.device;
    scoreHint.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'scoreboard'), el('span', { text: 'Scores' })]));
  }
  // Glyphs follow the device, pad family and bindings.
  deps.services.input.onChange(() => {
    if (!shown) return;
    renderScoreHint();
    renderPractice();
  });

  function updateTop(view: FrameView): void {
    const scoring = view.clock.cap !== Infinity && Number.isFinite(view.clock.cap);
    const info = deps.services.practiceInfo();
    const text = view.golden ? 'GOLDEN ROUND' : view.source === 'online' && view.totalRounds > 0 ? `ROUND ${view.roundIndex + 1}/${view.totalRounds}` : info.active ? (info.freePlay ? 'FREE PLAY' : 'PRACTICE') : MODE_NAMES[view.modeId].toUpperCase();
    setText(roundPill, text);
    toggleClass(roundPill, 'gold', view.golden);
    setHidden(timer, !scoring);
    setHidden(phaseChip, !scoring);
    if (scoring) {
      const left = Math.max(0, view.mode.timeLeftSec);
      const secs = Math.ceil(left);
      setText(timer, clockText(left));
      toggleClass(timer, 'urgent', left <= URGENT_SEC && view.roomPhase === RoomPhase.PLAY);
      if (secs !== lastTimerSec && left <= URGENT_SEC && left > 0 && view.roomPhase === RoomPhase.PLAY) replayClass(timer, 'tick');
      lastTimerSec = secs;
      const phase = view.roundTick < 0 ? RoundPhase.INTRO : view.clock.phase;
      setText(phaseChip, PHASE_NAMES[phase]);
      phaseChip.dataset.phase = String(phase);
      if (phase !== lastPhase) {
        lastPhase = phase;
        replayClass(phaseChip, 'swap');
      }
    }
    const mut = view.mutator;
    setHidden(mutChip, mut === MutatorId.NONE);
    if (mut !== MutatorId.NONE && mutChip.dataset.m !== String(mut)) {
      mutChip.dataset.m = String(mut);
      mutChip.replaceChildren(icon(MUTATOR_ICONS[mut]), el('span', { text: MUTATOR_NAMES[mut].toUpperCase() }));
    }
  }

  function updateNet(view: FrameView): void {
    const online = view.source === 'online' && view.net.online;
    setHidden(netIcon, !online);
    if (!online) return;
    const ms = Math.round(view.net.rtt);
    const cls = ms > 180 ? 'bad' : ms > 120 ? 'warn' : 'good';
    if (netIcon.dataset.q !== cls) {
      netIcon.dataset.q = cls;
      netIcon.replaceChildren(pingBars(ms), el('span', { class: 'hn-ms' }));
    }
    const msNode = netIcon.querySelector('.hn-ms');
    if (msNode) setText(msNode, `${ms} ms`);
  }

  return {
    el: root,
    onShow() {
      shown = true;
      feed.clear();
      banners.reset();
      roundId = -1;
      introFor = -1;
      lastPhase = -1;
      renderPractice();
      renderScoreHint();
    },
    onHide() {
      shown = false;
      // Off the round (lobby, tally, podium): the renderer's own defaults.
      sentInsets = null;
      deps.services.setHudInsets(null);
      deps.services.setNameplateRects(null);
    },
    onNav(action: NavAction) {
      if (bonkCam.active() && action === 'confirm') {
        bonkCam.skip();
        return true;
      }
      if (action === 'scoreboard' && deps.services.input.device === 'touch') {
        scoreboard.toggle();
        return true;
      }
      return false;
    },
    update(view) {
      const now = performance.now();
      const dt = lastFrame > 0 ? (now - lastFrame) / 1000 : 0;
      lastFrame = now;
      last = view;
      if (view.roundId !== roundId) startRound(view);
      // The round's intro card (§9.5 INTRO): mode, map, rules, golden.
      if (view.roundTick < -30 && introFor !== view.roundId && view.modeId >= 2) {
        introFor = view.roundId;
        const mut = view.mutator !== MutatorId.NONE ? `Mutator: ${MUTATOR_NAMES[view.mutator]}` : null;
        banners.intro(view.modeId, view.mapId, view.golden, mut);
      }
      // GAME! (§11.5) is the Renderer's stamp, timed with its slow motion and desaturation: a
      // MATCH POINT or SUDDEN DEATH sweep still running gives way to it.
      if (view.grade === 'game') banners.endSweep();
      const cb = colorblind();
      updateTop(view);
      mode.update(view, cb);
      updateNet(view);
      const me = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : null;
      dial.update(me && me.active ? me : null, now, dt);
      const compact = compactLayout();
      feed.update(now, compact);
      banners.update(now);
      plates.update(view, deps.services.presented(), cb, deps.settings().gameplay.showDizzyNumbers);
      // First-time prompts teach the living bean; a ghost or a spectator gets none.
      if (me && me.active && !me.out && !me.ghost) prompt.update(view);
      else prompt.el.hidden = true;
      scoreboard.update(view, deps.services.input.scoreboardHeld, roundKos);
      bonkCam.update();
      toggleClass(root, 'replaying', deps.services.presented().source === 'replay');
      toggleClass(root, 'golden', view.golden);
      toggleClass(root, 'spectating', deps.services.spectating());
      setHidden(pauseBtn, deps.services.input.device === 'touch');
      setStyle(root, '--timer-sec', String(Math.ceil(view.mode.timeLeftSec)));
      reportInsets(sentInsets === null);
    },
  };
}
